import { PageHeader } from '../layout/PageHeader';
import { Button, Menu, MenuItem, useTheme } from '@mui/material';
import { ReactComponent as AddIcon } from '../../assets/icons/add-white.svg';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download-icon.svg';
import { Typography } from '../../components/Typography/Typography';
import { ContentWrapper, HeaderActionWrapper } from '../layout/styled';
import { DealsTable } from './components/DealsTable';
import { useCallback, useState } from 'react';
import { DealFormModal } from './components/DealFormModal/DealFormModal';
import { useAtomValue } from 'jotai';
import { isSmallScreenState } from '../../state/UIState';
import { useDeals } from '../../queries/hooks/useDeals';
import { exportDealsToCsv, exportDealsToXlsx } from '../../utils/dealsExport';
import { UserRole } from '../../types';

const HeaderAction = () => {
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const { data: deals } = useDeals(false, UserRole.ADMIN);
  const [addDealOpen, setAddDealOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleAddDealOpen = useCallback(() => {
    setAddDealOpen(true);
  }, []);

  const handleAddDealClose = useCallback(() => {
    setAddDealOpen(false);
  }, []);

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setAnchorEl(null);
  };

  const handleExportXlsx = () => {
    exportDealsToXlsx(deals ?? []);
    handleExportClose();
  };

  const handleExportCsv = () => {
    exportDealsToCsv(deals ?? []);
    handleExportClose();
  };

  return (
    <HeaderActionWrapper>
      <Button
        variant='outlined'
        startIcon={<DownloadIcon />}
        style={{
          backgroundColor: colors.surfaceBackground.bg1,
          marginBottom: isSmallScreen ? '10px' : '0px',
          height: '40px',
        }}
        onClick={handleExportClick}
      >
        Export
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleExportClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleExportXlsx}>
          <Typography
            variant='body'
            color={colors.text.main}
            style={{ marginLeft: '4px', marginBottom: '-2px' }}
          >
            Export as XLSX
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleExportCsv}>
          <Typography
            variant='body'
            color={colors.text.main}
            style={{ marginLeft: '4px', marginBottom: '-2px' }}
          >
            Export as CSV
          </Typography>
        </MenuItem>
      </Menu>
      <Button onClick={handleAddDealOpen} variant='contained' startIcon={<AddIcon />}>
        <Typography variant='h4' color={colors.textAccent.inverse} style={{ marginBottom: '-1px' }}>
          Add Deal
        </Typography>
      </Button>
      {addDealOpen && (
        <DealFormModal addDealOpen={addDealOpen} handleAddDealClose={handleAddDealClose} />
      )}
    </HeaderActionWrapper>
  );
};

export const Deals = () => {
  const isSmallScreen = useAtomValue(isSmallScreenState);

  return (
    <>
      <PageHeader
        title='Deals'
        description='Manage deals, their owners, and submitters'
        breadcrumbs={[{ title: 'Administration' }, { title: 'Deals' }]}
        actionComponent={<HeaderAction />}
      />
      <ContentWrapper isSmallScreen={isSmallScreen}>
        <DealsTable />
      </ContentWrapper>
    </>
  );
};
